import { Reservation, Table } from 'types/reservations';
import { ReservationShift } from 'types/shifts';
import { db } from 'utils/firebase';
import { ReservationWrite, writeReservations } from 'utils/reservationWrites';
import {
  buildTableBlockDoc,
  FULL_DAY_TABLE_BLOCK_TIME_FIELDS,
  groupTableBlocks,
  shiftTableBlockTimeFields,
  TableBlockTimeFields,
  tableBlockDocumentId,
  tableBlockWindowKey,
} from 'utils/tableBlocks';

/**
 * Turn the timeline state of the block dialog into Firestore writes: add what
 * became blocked, delete what became free. Shared by the table sidebar and the
 * timetable so both write the same documents. Commits everything as one
 * atomic batch and rejects on failure so callers can surface an error.
 */
const applyTableBlockChanges = ({
  fullDay,
  shiftIds,
  removeOtherIds,
  blocks,
  shifts,
  restaurantId,
  date,
  table,
  uid,
  now,
}: {
  fullDay: boolean;
  shiftIds: string[];
  removeOtherIds: string[];
  blocks: Reservation[];
  shifts: ReservationShift[];
  restaurantId: string;
  date: string;
  table: Pick<Table, 'name' | 'space' | 'occassions'>;
  uid: string;
  now: number;
}) => {
  // Same table filter as the dialog, so both classify blocks identically —
  // otherwise saving could delete a block the dialog never showed.
  const existing = groupTableBlocks(blocks, shifts, table);
  // One atomic write: splitting a full-day block into shift blocks must not
  // end half-done (day block deleted, shift blocks missing = table bookable).
  const writes: ReservationWrite[] = [];

  const remove = (id: string) => writes.push({ op: 'delete', id });

  const add = (timeFields: TableBlockTimeFields) =>
    writes.push({
      op: 'set',
      id: tableBlockDocumentId({
        restaurantId,
        date,
        tableName: table.name,
        timeFields,
      }),
      data: buildTableBlockDoc({
        restaurantId,
        date,
        tableName: table.name,
        space: table.space,
        uid,
        now,
        timeFields,
      }),
    });

  removeOtherIds.forEach(remove);

  if (fullDay && !existing.fullDay) add(FULL_DAY_TABLE_BLOCK_TIME_FIELDS);
  if (!fullDay && existing.fullDay) remove(existing.fullDay.id);

  // Shifts that run at the same time share one window, so collect windows and
  // not shift ids — otherwise three parallel shifts write three identical
  // documents, which then look like a triple booking.
  const wanted: Record<string, TableBlockTimeFields> = {};

  // A full-day block already covers every shift, so per-shift blocks are only
  // written when the day itself stays open.
  if (!fullDay) {
    shiftIds.forEach((shiftId) => {
      const timeFields = shiftTableBlockTimeFields(shifts, shiftId);
      if (timeFields) wanted[tableBlockWindowKey(timeFields)] = timeFields;
    });
  }

  Object.keys(wanted).forEach((key) => {
    if (!existing.byWindowKey[key]) add(wanted[key]);
  });

  Object.keys(existing.byWindowKey).forEach((key) => {
    if (!wanted[key]) remove(existing.byWindowKey[key].id);
  });

  return writes.length ? writeReservations(restaurantId, writes, [date]) : Promise.resolve([]);
};

/**
 * The default path without the experimental flag: one click blocks the whole
 * day, one click frees the table again. Staff mid-service must not pay a
 * dialog for that. Every block on the table goes, so a shift block left over
 * from the dialog cannot survive under the new day block. `blocked` is the
 * state the user sees in the shift they are looking at, and the click reacts
 * to that, not to blocks in shifts that are currently out of view.
 */
export const toggleFullDayTableBlock = ({
  blocked,
  blocks,
  restaurantId,
  date,
  table,
  uid,
  now,
}: {
  blocked: boolean;
  blocks: Reservation[];
  restaurantId: string;
  date: string;
  table: Pick<Table, 'name' | 'space'>;
  uid: string;
  now: number;
}) => {
  const writes: ReservationWrite[] = blocks.map((block) => ({ op: 'delete', id: block.id }));

  if (!blocked) {
    writes.push({
      op: 'set',
      id: tableBlockDocumentId({
        restaurantId,
        date,
        tableName: table.name,
        timeFields: FULL_DAY_TABLE_BLOCK_TIME_FIELDS,
      }),
      data: buildTableBlockDoc({
        restaurantId,
        date,
        tableName: table.name,
        space: table.space,
        uid,
        now,
        timeFields: FULL_DAY_TABLE_BLOCK_TIME_FIELDS,
      }),
    });
  }

  return writes.length ? writeReservations(restaurantId, writes, [date]) : Promise.resolve([]);
};

export const removeTableBlock = (blockId: string) =>
  db.collection('requestsV03').doc(blockId).delete();

export default applyTableBlockChanges;
